/**
 * Sets document.title + <meta name="description">, optionally injects
 * a <meta name="robots" content="noindex,nofollow"> for private pages.
 * Very lightweight — no react-helmet dep.
 */
import { useEffect } from "react";

export function useDocumentSeo({ title, description, noindex = false, canonical = null } = {}) {
    useEffect(() => {
        const prevTitle = document.title;

        if (title) document.title = title;

        // description
        let descMeta = document.querySelector('meta[name="description"]');
        const prevDesc = descMeta?.getAttribute("content") || "";
        if (description) {
            if (!descMeta) {
                descMeta = document.createElement("meta");
                descMeta.setAttribute("name", "description");
                document.head.appendChild(descMeta);
            }
            descMeta.setAttribute("content", description);
        }

        // robots
        let robotsMeta = document.querySelector('meta[name="robots"]');
        const prevRobots = robotsMeta?.getAttribute("content") || "";
        if (!robotsMeta) {
            robotsMeta = document.createElement("meta");
            robotsMeta.setAttribute("name", "robots");
            document.head.appendChild(robotsMeta);
        }
        robotsMeta.setAttribute("content", noindex ? "noindex,nofollow" : "index,follow");

        // canonical
        let linkCanonical = document.querySelector('link[rel="canonical"]');
        const prevCanonical = linkCanonical?.getAttribute("href") || "";
        if (canonical) {
            if (!linkCanonical) {
                linkCanonical = document.createElement("link");
                linkCanonical.setAttribute("rel", "canonical");
                document.head.appendChild(linkCanonical);
            }
            linkCanonical.setAttribute("href", canonical);
        }

        return () => {
            document.title = prevTitle;
            if (description && descMeta) descMeta.setAttribute("content", prevDesc);
            if (robotsMeta) robotsMeta.setAttribute("content", prevRobots || "index,follow");
            if (canonical && linkCanonical) linkCanonical.setAttribute("href", prevCanonical);
        };
    }, [title, description, noindex, canonical]);
}

/**
 * Injects a per-page <script type="application/ld+json"> block.
 * Accepts a single object or an array of schema objects.
 * Adds data-seo-jsonld="page" so we can safely swap between routes
 * without touching the site-wide block in index.html.
 */
export function useJsonLd(schema) {
    useEffect(() => {
        if (!schema) return;
        const payload = Array.isArray(schema) ? schema : [schema];
        const nodes = payload.map((obj) => {
            const s = document.createElement("script");
            s.type = "application/ld+json";
            s.setAttribute("data-seo-jsonld", "page");
            s.textContent = JSON.stringify(obj);
            document.head.appendChild(s);
            return s;
        });
        return () => nodes.forEach((n) => n.parentNode && n.parentNode.removeChild(n));
    }, [JSON.stringify(schema)]);
}
